/**
 * S4 — the onboarding hero turn: a REAL /chat/stream turn that drives the
 * Explorer, plus the safety net that keeps the tour intact when the model
 * does not cooperate.
 *
 * Why a watcher instead of trusting the turn: the hero moment must never
 * leave the user in a broken or empty state. So the tour fires the turn and
 * then waits for EVIDENCE that Ion actually drove something — and advances
 * gracefully (softer caption) if that evidence never arrives.
 *
 * Why the success signal is `commandProvenance` flipping to source:'ion'
 * and NOT the filtered-cell count: the count is ambiguous. The user can
 * filter during the interactive phase, and a basis switch triggers a
 * metadata reload that re-inits the range signals — the count changes with
 * no Ion action behind it. Provenance is unambiguous. The count may only
 * refine the CAPTION.
 */
import { effect } from '@preact/signals'
import { commandProvenance } from '../state/commandLayer.js'
import {
  autoSendDraft,
  chatMessages,
  chatStreaming,
  draftInput,
  streamingMessageId,
} from '../state/chatStore.js'
import { stopChatStream } from '../hooks/useChatStream.js'
import { filteredCells } from '../state/globalSignals.js'

/**
 * The prompt the tour sends on the user's behalf. Deliberately explicit
 * about gravimetric: "250 Wh/kg" is only meaningful on the gravimetric
 * basis, and asking Ion to switch it (rather than pre-scripting the switch
 * ourselves) keeps the whole drive real — Ion's own set_context call also
 * counts as the success signal.
 */
export const CANONICAL_INTRO_PROMPT =
  'Switch to gravimetric and show me 21700 cells above 250 Wh/kg, sorted by peak power.'

/**
 * A real tool turn needs first-token latency plus a tool round-trip. 8-10s
 * would abort turns that are genuinely about to succeed.
 */
export const INTRO_TURN_TIMEOUT_MS = 14000

/**
 * Pure outcome decision — everything the watcher knows in one observation.
 * Returns null while the turn is still undecided.
 *
 * @param {object} o
 * @param {number} o.startSeq            commandProvenance.seq before firing
 * @param {number} o.provenanceSeq       current commandProvenance.seq
 * @param {string|null} o.provenanceSource current commandProvenance.source
 * @param {boolean} o.streamingSeen      chatStreaming was true at some point
 * @param {boolean} o.streaming          chatStreaming right now
 * @param {string|null} o.lastAssistantStatus status of the newest assistant message
 * @returns {'driven'|'error'|'no-drive'|null}
 */
export function classifyIntroTurn({
  startSeq,
  provenanceSeq,
  provenanceSource,
  streamingSeen,
  streaming,
  lastAssistantStatus,
}) {
  // Success takes precedence: a turn that drove the grid AND then errored
  // still delivered the hero moment.
  if (provenanceSeq > startSeq && provenanceSource === 'ion') return 'driven'
  if (!streamingSeen || streaming) return null
  if (lastAssistantStatus === 'error') return 'error'
  return 'no-drive'
}

// FIX 3 (S4 fix-wave): liveness tracked by IDENTITY, not a module-level
// boolean. A boolean can't tell "our turn ended" apart from "a DIFFERENT
// turn is now live" — both look like "some turn is live" to a flag. Two
// concrete failures that shape follow from that:
//   - a FOREIGN stream dies: the user starts their own turn while this flag
//     is still true (today only prevented by the tour overlay swallowing
//     clicks outside the cutout — a CSS property, not a code guard) and a
//     teardown aborts THEIR stream.
//   - our OWN turn becomes unabortable: Back→Drive→Back→Next while a turn
//     streams can end up settling `_live = false` in the same notification
//     batch that starts a second pending onboarding turn, before that
//     second turn's watcher effect has run — its `_live` would read false
//     even though it just started.
// The turn's identity is `streamingMessageId` (chatStore.js) — but that id
// doesn't exist yet at fireIntroTurn() time (it's assigned only once
// startAssistantMessage() runs, after the autosend effect actually consumes
// the draft). So `_liveMessageId` starts at the PENDING sentinel (fired,
// no stream observed yet — still correctly "ours" to abort) and is
// upgraded to the real id the first time the watcher effect below observes
// streaming start; `abortIonIntroTurnIfLive()` only ever acts on PENDING or
// an EXACT id match, so it can never touch a stream it didn't start.
const PENDING = Symbol('ion-intro-turn-pending')
let _liveMessageId = null
let _outcome = null

// Task 1 (defect A2): the hero caption's match count, captured ONCE at the
// exact moment the turn settles as 'driven' — the only moment the number is
// provably Ion's (see classifyIntroTurn's doc comment on why provenance,
// not the count, is the success signal). `filteredCells.value.length` at
// RENDER time is not safe to read directly: on a Back re-entry after the
// user has filtered further themselves, the live count has moved and no
// longer describes what Ion did. Reset alongside `_outcome` in
// fireIntroTurn() so a second run (Back → Drive again, guarded by
// `_firedThisRun` at the call site) never carries a stale prior count.
let _frozenCount = null

// Only the FIRST turn per user is token-exempt — every turn after that is
// real spend charged to the user's own budget. Pre-S4 "Back" was free (it
// replayed a script); since the drive step fires a REAL turn, re-entering it
// must not fire a second paid one (nor leak a second watcher effect).
//
// The flag lives HERE, with the turn, not in tourDefinitions.js. The only
// thing that legitimately means "a new run" is startTour(), and tourState.js
// can import THIS module statically — it has no dependency on tourState, so
// there is no cycle — whereas tourDefinitions.js imports tourState and so
// cannot be imported back from it. Two earlier attempts to bridge that cycle
// each caused order-dependent test failures: a fire-and-forget
// `import().then()` (unbounded resolution — landed inside unrelated later
// test files) and a module-level `effect()` on `activeTour` (a never-disposed
// subscriber installed at import time, which threw at import in files that
// mock tourState partially). A plain synchronous call has neither failure
// mode.
//
// Deliberately NOT reset from intro-greet's beforeShow: prevStep() can walk
// back to step 0, so that would re-arm the guard and let a Back→forward
// round-trip fire a second paid turn after all.
let _firedThisRun = false

/** True once this tour run has already fired its hero turn. */
export function hasIntroTurnFiredThisRun() {
  return _firedThisRun
}

/** Re-arm the per-run guard. Called only by startTour() (tourState.js). */
export function resetIntroTurnRun() {
  _firedThisRun = false
}

/** Last settled outcome — read by the drive step's caption. */
export function introTurnOutcome() {
  return _outcome
}

/**
 * The match count frozen at the moment the turn settled 'driven', or null if
 * the turn never drove anything (or hasn't settled yet). Read by the drive
 * step's caption alongside introTurnOutcome() — see the doc comment on
 * `_frozenCount` above for why this may not be re-derived from the live
 * signal.
 */
export function introTurnFrozenCount() {
  return _frozenCount
}

/**
 * Seed the real turn through the existing autoSendDraft seam (the same
 * mechanism guided cell-selection uses) and arm the liveness flag.
 *
 * FIX 1 (S4 fix-wave): also seeds `draftInput` with the same prompt, exactly
 * like the guided cell-selection door (CellSelectionEntry.jsx's
 * seedGuidedSelection) does. Without it, the autosend effect
 * (IonSurface.jsx) never fires on a first-run intro — `canSend`
 * (chatStore.js) requires `draftInput.value.trim().length > 0`, so an empty
 * composer silently blocks the whole turn until the 14s timeout degrades to
 * the softer caption. IonSurface's autosend effect clears `draftInput`
 * itself once it fires, so this is a one-shot seed, not a lasting value.
 */
export function fireIntroTurn({ contextHint = null } = {}) {
  _firedThisRun = true
  _outcome = null
  _frozenCount = null
  _liveMessageId = PENDING
  draftInput.value = CANONICAL_INTRO_PROMPT
  autoSendDraft.value = {
    text: CANONICAL_INTRO_PROMPT,
    contextHint,
    onboarding: true,
    example: true,
  }
}

/**
 * Resolve as soon as the turn's outcome is decided, or on timeout.
 * Never rejects — the caller's job is to advance the tour either way.
 */
export function awaitIntroTurnOutcome() {
  const startSeq = commandProvenance.value?.seq ?? 0
  let streamingSeen = false
  return new Promise((resolve) => {
    let settled = false
    let disposeFn = null
    let timer = null

    const settle = (outcome) => {
      if (settled) return
      settled = true
      _outcome = outcome
      // Task 1: capture the count HERE, at the exact instant the outcome
      // becomes 'driven' — this is the only point in the whole turn where
      // filteredCells.value.length is provably describing what Ion just
      // did. One tick later the user could already be typing a follow-up.
      if (outcome === 'driven') _frozenCount = filteredCells.value.length
      if (timer) clearTimeout(timer)
      // Deferred so the effect is never disposed from inside its own run.
      // FIX 7 (S4 fix-wave): `disposeFn` is only assigned AFTER `effect()`
      // below returns — if the effect's SYNCHRONOUS first run itself calls
      // settle() (outcome already decided with no async wait at all),
      // `disposeFn` is still null at this exact moment and this check would
      // silently skip disposal forever, since nothing re-checks it later.
      // Unreachable today (the first run always observes `streaming: false,
      // streamingSeen: false` → classifyIntroTurn returns null), but a
      // future refactor could change that — the post-assignment check right
      // after `effect()` below covers it regardless.
      if (disposeFn) setTimeout(disposeFn, 0)
      resolve(outcome)
    }

    disposeFn = effect(() => {
      const prov = commandProvenance.value
      const streaming = chatStreaming.value
      const msgId = streamingMessageId.value
      if (streaming) {
        streamingSeen = true
        // FIX 3: upgrade the PENDING sentinel to the REAL streamingMessageId
        // the first time we observe this turn actually streaming. Never
        // re-captured once set — a later id change means a DIFFERENT
        // message (e.g. a follow-up turn in the same conversation), not
        // ours.
        if (_liveMessageId === PENDING && msgId != null) _liveMessageId = msgId
      }
      // Liveness follows chatStreaming, not the outcome: Ion may still be
      // streaming prose (and could still emit another tool call) after the
      // first command lands, so a later teardown must not abort anything
      // once the stream is genuinely over — but not a moment before.
      if (streamingSeen && !streaming) markIntroTurnEnded()
      const msgs = chatMessages.value
      let lastAssistantStatus = null
      for (let i = msgs.length - 1; i >= 0; i -= 1) {
        if (msgs[i].role === 'assistant') {
          lastAssistantStatus = msgs[i].status
          break
        }
      }
      const outcome = classifyIntroTurn({
        startSeq,
        provenanceSeq: prov?.seq ?? 0,
        provenanceSource: prov?.source ?? null,
        streamingSeen,
        streaming,
        lastAssistantStatus,
      })
      if (outcome) settle(outcome)
    })
    // FIX 7: catch the case described in `settle` above — dispose right
    // away instead of leaking the effect forever.
    if (settled) setTimeout(disposeFn, 0)

    timer = setTimeout(() => settle('timeout'), INTRO_TURN_TIMEOUT_MS)
  })
}

/**
 * Clear a still-pending onboarding `autoSendDraft` WITHOUT touching a live
 * stream. Split out of `abortIonIntroTurnIfLive()` below (S4 fix-wave, Fix
 * B) for intro-handover's `beforeShow`, which no longer aborts the stream at
 * all — the driven turn is now let run to completion across the handover
 * (see tourDefinitions.js's intro-handover doc comment for why) — but a
 * draft that never even passed the autosend guards (e.g. exhausted quota)
 * still must not survive to fire later on whatever page the user has since
 * navigated to.
 */
export function clearPendingIntroDraft() {
  if (autoSendDraft.peek()?.onboarding) autoSendDraft.value = null
}

/**
 * Abort the onboarding stream — MUST run BEFORE restoreIonIntroBaseline().
 *
 * A real stream bypasses the abort-generation machinery (ionIntroGen.js's
 * `bumpIonIntroGen`) the scripted demo used. On a slow turn the sequence
 * timeout -> advance -> restore could be
 * followed by the turn's tool call landing AFTER the restore, mutating the
 * grid post-restore and stranding the user permanently (restore is by then
 * a no-op). A client abort stops artifact processing, so nothing applies
 * afterwards.
 *
 * Guarded on `_liveMessageId` so this can be called from the shared
 * teardown path (skip / ESC / complete) WITHOUT ever aborting a stream the
 * user started themselves — by tour completion the onboarding turn is long
 * settled and `_liveMessageId` is null. FIX 3 (S4 fix-wave): the guard is by
 * IDENTITY, not a boolean — it fires `stopChatStream()` only when
 * `_liveMessageId` is still the PENDING sentinel (fired, no stream observed
 * yet) or exactly matches the CURRENT `streamingMessageId`, so it can never
 * reach into a stream this turn didn't start (a foreign turn the user
 * started themselves, or a later onboarding turn after this one already
 * ended).
 *
 * S4 fix-wave, Fix B: the ONLY remaining caller is
 * `restoreIonIntroBaseline()`'s skip/ESC-before-handover teardown path — the
 * user genuinely bailed there, so aborting the live turn is correct.
 * intro-handover's own `beforeShow` (a genuine takeover, not a bail) calls
 * `clearPendingIntroDraft()` above instead, letting the turn run to
 * completion.
 */
export function abortIonIntroTurnIfLive() {
  clearPendingIntroDraft()
  if (_liveMessageId == null) return
  if (_liveMessageId !== PENDING && streamingMessageId.peek() !== _liveMessageId) return
  _liveMessageId = null
  stopChatStream()
}

/** Called by the watcher wiring once the onboarding stream is truly over. */
export function markIntroTurnEnded() {
  _liveMessageId = null
}
